import { useCallback, useContext } from "react";
import { QueryParamsContext } from "../../../context/QueryParamsContext";
import { findCashFlow } from "../../../repositories/qerpapi/erp/relatorio/FluxoDeCaixaRepository";
import { notify } from "../../../utils/notify";
import { useDashBoardFinanceiroService } from "../contexts/DashBoardFinanceiroContext";

export default function useDashBoardFinanceiroController() {
  const { queryParams } = useContext(QueryParamsContext);

  const {
    cashFlowChartInfo,
    setCashFlowChartInfo,
    billsChartsInfo,
    setBillsChartsInfo,
    billToPayRefetch,
    billToReceiveRefetch,
    setBillsToPayChartView,
    setBillsToReceiveChartView,
    setBillsToPayGroupPeriod,
    setBillsToReceiveGroupPeriod,
    toggleCashFlowLoading,
  } = useDashBoardFinanceiroService();

  const handleCheckCashFlowOptions = (options = []) => {
    const checkedOptions = cashFlowChartInfo.checkDisplayOptions.map(
      (option) => ({
        ...option,
        check: options.includes(option.id),
      })
    );

    setCashFlowChartInfo((prev) => ({
      ...prev,
      checkDisplayOptions: checkedOptions,
    }));
  };

  const handleRadioCashFlowOptions = (optionId) => {
    if (!optionId) return;

    const copyCashFlowChartInfo = {
      ...cashFlowChartInfo,
      groupPeriod: optionId,
    };

    setCashFlowChartInfo(copyCashFlowChartInfo);
  };

  const refreshCashFlowChart = useCallback(
    async (dashBoardFilters) => {
      if (!queryParams?.empresa_id) return;
      toggleCashFlowLoading(true);
      if (!dashBoardFilters?.multicompany) {
        dashBoardFilters["multicompany"] = queryParams?.empresa_id;
      }

      var displayOptions = {};
      [...cashFlowChartInfo.checkDisplayOptions].forEach((options) => {
        displayOptions[options.id] = options.check;
      });

      const jsonResult = await findCashFlow(
        {
          period: "custom",
          startDate: dashBoardFilters.startDate,
          endDate: dashBoardFilters.endDate,
          movement: "all",
          multicompany: dashBoardFilters.multicompany,
          reportType: "consolidated",
          group: cashFlowChartInfo.groupPeriod || "day",
          showPreviousBalance: displayOptions["previousBalance"],
          showNotPayedAccounts: displayOptions["notPayedAccounts"],
          showNotReceivedTitles: displayOptions["notReceivedTitles"],
        },
        queryParams.gfAccessToken
      );

      if (jsonResult.success) {
        var cashFlowData = [];
        jsonResult.data.movements?.forEach((moviment) => {
          cashFlowData.push({
            date: moviment.period?.startDate,
            balance: moviment.total?.balance,
            toReceive: moviment.total?.credit,
            toPay: moviment.total?.debit,
          });
        });

        setCashFlowChartInfo((current) => ({
          ...current,
          data: cashFlowData,
        }));
      } else {
        notify("Erro ao pesquisar dados!");
      }
      toggleCashFlowLoading(false);
    },
    [
      queryParams?.empresa_id,
      queryParams.gfAccessToken,
      toggleCashFlowLoading,
      cashFlowChartInfo.checkDisplayOptions,
      cashFlowChartInfo.groupPeriod,
      setCashFlowChartInfo,
    ]
  );

  const handleRadioIntervalBillsOptions = (optionId) => {
    var copyBillsChartsInfo = {
      ...billsChartsInfo,
      groupPeriod: optionId,
    };

    setBillsChartsInfo(copyBillsChartsInfo);
  };

  const handleRadioFilterBillsOptions = (optionId) => {
    var copyBillsChartsInfo = {
      ...billsChartsInfo,
      filterPeriod: optionId,
    };

    const copyRadioDateDisplayOptions = [
      ...copyBillsChartsInfo.radioDateDisplayOptions,
    ];

    const switchRadioInvervalByFilter = {
      actual: () => {
        copyBillsChartsInfo.radioDateDisplayOptions.forEach(
          (option, optionIndex) => {
            switch (option.id) {
              case "date":
                option.disabled = true;
                break;
              case "week":
                option.disabled = true;
                break;
              case "month":
                option.disabled = false;
                break;
              default:
                option.disabled = false;
                break;
            }

            copyRadioDateDisplayOptions[optionIndex] = option;
          }
        );

        copyBillsChartsInfo = {
          ...copyBillsChartsInfo,
          radioDateDisplayOptions: copyRadioDateDisplayOptions,
          groupPeriod: "month",
        };
      },
      custom: () => {
        copyBillsChartsInfo.radioDateDisplayOptions.forEach(
          (option, optionIndex) => {
            option.disabled = false;
            copyRadioDateDisplayOptions[optionIndex] = option;
          }
        );

        copyBillsChartsInfo = {
          ...copyBillsChartsInfo,
          radioDateDisplayOptions: copyRadioDateDisplayOptions,
        };
      },
    };

    switchRadioInvervalByFilter[optionId]();

    setBillsChartsInfo(copyBillsChartsInfo);
  };

  const refreshBillsCharts = useCallback(async () => {
    setTimeout(
      async () =>
        await Promise.all([billToReceiveRefetch(), billToPayRefetch()]),
      100
    );
  }, [billToPayRefetch, billToReceiveRefetch]);

  const handleChangeBillsToPayChartView = (event, newChartView) => {
    if (newChartView) setBillsToPayChartView(newChartView);
  };

  const handleChangeBillsToReceiveChartView = (event, newChartView) => {
    if (newChartView) setBillsToReceiveChartView(newChartView);
  };

  const handleChangeBillsToPayGroupPeriod = (event, newGroupPeriod) => {
    if (newGroupPeriod) setBillsToPayGroupPeriod(newGroupPeriod);
  };

  const handleChangeBillsToReceiveGroupPeriod = (event, newGroupPeriod) => {
    if (newGroupPeriod) setBillsToReceiveGroupPeriod(newGroupPeriod);
  };

  return {
    handleCheckCashFlowOptions,
    handleRadioCashFlowOptions,
    refreshCashFlowChart,
    refreshBillsCharts,
    handleRadioIntervalBillsOptions,
    handleRadioFilterBillsOptions,
    handleChangeBillsToPayChartView,
    handleChangeBillsToReceiveChartView,
    handleChangeBillsToPayGroupPeriod,
    handleChangeBillsToReceiveGroupPeriod,
  };
}
